import { Component, effect, input, OnDestroy, OnInit, output, Self } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ValidatorFn } from '@angular/forms';
import { SubscriptSizing } from '@angular/material/form-field';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

import { ReplaySubject, Subscription } from 'rxjs';

import { UiPalette } from '../../../types/UiPalette.type';

@Component({
  selector: 'eiq-input',
  templateUrl: 'input.component.html',
  styleUrls: ['input.component.scss']
})
export class InputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  autofocus = input<boolean>(false);
  descriptionHint = input<string>('');
  type = input<'text' | 'number' | 'email' | 'tel' | 'password'>('text');
  label = input<string>();
  hint = input<string>();
  placeholder = input<string>();
  prefix = input<string>();
  suffix = input<string>();
  maxLength = input<number>();
  min = input<number>();
  max = input<number>();
  holdRequiredPlaceholder = input<SubscriptSizing>('dynamic');
  testId = input<string>();
  tooltip = input<string>(null);
  color = input<UiPalette>();

  keyupEnter = output<void>();
  onFocus = output<void>();
  onBlur = output<void>();

  onChange: (value: string) => void;
  onTouched: () => void;

  control: FormControl;
  currentValue: string;
  faInfoCircle = faInfoCircle;

  private input$ = new ReplaySubject(1);
  private subscriptions: Subscription[] = [];
  private isFocused = false;
  private hasPendingMinMaxValidation = false;
  private shouldValidateMinMax = false;

  constructor(@Self() public readonly ngControl: NgControl) {
    this.ngControl.valueAccessor = this;

    effect(() => {
      this.min();
      this.max();

      if (!this.control) {
        return;
      }

      if (this.isFocused) {
        this.hasPendingMinMaxValidation = true;
        return;
      }

      this.control.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;

    if (this.type() === 'number') {
      this.control.addValidators(this.noLeadingZeroValidator);
    }
    this.control.addValidators(this.minMaxValidator);

    this.subscriptions.push(
      this.input$.subscribe((i: string) => {
        this.onTouched();
        this.onChange(i);
      })
    );
  }

  handleBlur(): void {
    this.isFocused = false;
    this.shouldValidateMinMax = true;

    if (this.hasPendingMinMaxValidation) {
      this.hasPendingMinMaxValidation = false;
    }

    this.control?.updateValueAndValidity({ emitEvent: false });

    this.onBlur.emit();
  }

  handleFocus(): void {
    this.isFocused = true;
    this.shouldValidateMinMax = false;
    this.control?.updateValueAndValidity({ emitEvent: false });
    this.onFocus.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(i: string): void {
    this.currentValue = i ?? '';
  }

  setValue($event: any): void {
    this.currentValue = $event.target.value;
    this.input$.next($event.target.value);
  }

  clearValue(): void {
    this.currentValue = '';
    this.input$.next('');
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.type() !== 'number') {
      return;
    }

    if (event.key.toLowerCase() === 'e') {
      event.preventDefault();
      return;
    }
  }

  setDisabledState?(_isDisabled: boolean): void {
    // Implement if needed
  }

  private minMaxValidator: ValidatorFn = (control: AbstractControl) => {
    if (!this.shouldValidateMinMax) {
      return null;
    }

    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return null;
    }

    const min = this.min();
    if (min !== undefined && min !== null && numericValue < min) {
      return { min: { min, actual: numericValue } };
    }

    const max = this.max();
    if (max !== undefined && max !== null && numericValue > max) {
      return { max: { max, actual: numericValue } };
    }

    return null;
  };

  private noLeadingZeroValidator = (control: AbstractControl): { leadingZero: boolean } | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const stringValue = String(value);
    if (/^0[^.]/.test(stringValue)) {
      return { leadingZero: true };
    }
    return null;
  };
}
